'use client';
import { cn } from '@util/index';
import { Fragment, useEffect, useRef, useState } from 'react';

const DEFAULT_MARQUEE_SPEED = 0.3;
const MOBILE_STEP_INTERVAL_MS = 4000;
const TOUCH_RESUME_DELAY_MS = 2000;

interface MarqueeCarouselProps<T> {
  items: T[];
  /** Rendered cards must be fixed-width with `shrink-0 snap-center`. */
  renderItem: (item: T, index: number) => React.ReactNode;
  /** Extra classes for the scrollable row (gaps, paddings). */
  contentClassName?: string;
  /** `from-*` color matching the section background, for the desktop edge fades. */
  fadeClassName?: string;
  /** Dot color for the mobile stepper, matching the section foreground. */
  dotClassName?: string;
  /** Desktop marquee speed in px per frame. */
  speed?: number;
}

/**
 * Infinite horizontal carousel: continuous marquee on desktop (pauses on
 * hover/touch), one-card-at-a-time auto stepper with dots on mobile.
 */
const MarqueeCarousel = <T,>({
  items,
  renderItem,
  contentClassName,
  fadeClassName = 'from-white',
  dotClassName = 'bg-black',
  speed = DEFAULT_MARQUEE_SPEED,
}: MarqueeCarouselProps<T>) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const pausedRef = useRef(false);
  const resumeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // fractional scroll position; scrollLeft alone rounds to whole pixels and
  // swallows sub-pixel increments, freezing the marquee at low speeds
  const marqueePosRef = useRef(0);
  // card stride cached outside the animation loop so per-frame work never
  // reads layout; refreshed by the ResizeObserver below
  const strideRef = useRef(0);
  const isDesktopRef = useRef(false);
  const startMarqueeRef = useRef(() => {});
  const stopMarqueeRef = useRef(() => {});
  const [activeIndex, setActiveIndex] = useState(0);

  // second copy gives the auto-scroll a seamless point to loop back to
  const loopedItems = [...items, ...items];

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = () => {
      const cards = el.children;
      strideRef.current =
        cards.length < 2
          ? el.scrollWidth
          : (cards[1] as HTMLElement).offsetLeft -
            (cards[0] as HTMLElement).offsetLeft;
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [items.length]);

  // desktop: continuous marquee; the frame loop only runs while on the
  // desktop breakpoint and unpaused, and re-syncs to whatever position
  // manual scrolling left behind before restarting
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const desktop = window.matchMedia('(min-width: 768px)');
    isDesktopRef.current = desktop.matches;
    let frame = 0;
    let running = false;

    const step = () => {
      const loopWidth = strideRef.current * items.length;
      if (loopWidth > 0) {
        marqueePosRef.current += speed;
        if (marqueePosRef.current >= loopWidth) {
          marqueePosRef.current -= loopWidth;
        }
        el.scrollLeft = marqueePosRef.current;
      }
      frame = requestAnimationFrame(step);
    };
    const start = () => {
      if (running || !desktop.matches || pausedRef.current) return;
      running = true;
      marqueePosRef.current = el.scrollLeft;
      frame = requestAnimationFrame(step);
    };
    const stop = () => {
      running = false;
      cancelAnimationFrame(frame);
    };
    const onBreakpointChange = () => {
      isDesktopRef.current = desktop.matches;
      if (desktop.matches) start();
      else stop();
    };

    startMarqueeRef.current = start;
    stopMarqueeRef.current = stop;
    desktop.addEventListener('change', onBreakpointChange);
    start();
    return () => {
      stop();
      desktop.removeEventListener('change', onBreakpointChange);
    };
  }, [items.length, speed]);

  // mobile: advance one card at a time on an interval
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const advance = () => {
      if (isDesktopRef.current || pausedRef.current) return;
      const stride = strideRef.current;
      if (stride === 0) return;
      const loopWidth = stride * items.length;
      if (el.scrollLeft >= loopWidth) {
        el.scrollLeft -= loopWidth;
      }
      el.scrollBy({ left: stride, behavior: 'smooth' });
    };
    const interval = setInterval(advance, MOBILE_STEP_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [items.length]);

  const onScroll = () => {
    const el = scrollRef.current;
    // the dot stepper is mobile-only; skip the work while the desktop
    // marquee floods this handler with its own scrollLeft writes
    if (!el || isDesktopRef.current || strideRef.current === 0) return;
    const index = Math.round(el.scrollLeft / strideRef.current);
    setActiveIndex(index % items.length);
  };

  const pause = () => {
    if (resumeTimeoutRef.current !== null)
      clearTimeout(resumeTimeoutRef.current);
    pausedRef.current = true;
    stopMarqueeRef.current();
  };
  const resume = () => {
    pausedRef.current = false;
    startMarqueeRef.current();
  };
  const resumeAfterTouch = () => {
    if (resumeTimeoutRef.current !== null)
      clearTimeout(resumeTimeoutRef.current);
    resumeTimeoutRef.current = setTimeout(resume, TOUCH_RESUME_DELAY_MS);
  };

  return (
    <div className="flex w-full flex-col gap-[3rem]">
      <div
        className="relative w-full"
        onMouseEnter={pause}
        onMouseLeave={resume}
        onTouchStart={pause}
        onTouchEnd={resumeAfterTouch}
      >
        <div
          ref={scrollRef}
          onScroll={onScroll}
          className={cn(
            'no-scrollbar flex w-full snap-x snap-mandatory overflow-x-auto md:snap-none',
            contentClassName
          )}
        >
          {loopedItems.map((item, i) => (
            <Fragment key={i}>{renderItem(item, i % items.length)}</Fragment>
          ))}
        </div>
        <div
          className={cn(
            'pointer-events-none absolute inset-y-0 left-0 hidden w-[8rem] bg-gradient-to-r to-transparent md:block',
            fadeClassName
          )}
        />
        <div
          className={cn(
            'pointer-events-none absolute inset-y-0 right-0 hidden w-[8rem] bg-gradient-to-l to-transparent md:block',
            fadeClassName
          )}
        />
      </div>
      <div className="flex justify-center gap-[0.5rem] md:hidden">
        {items.map((_, i) => (
          <div
            key={i}
            className={cn(
              'h-[0.75rem] rounded-full transition-all duration-300',
              dotClassName,
              i === activeIndex ? 'w-[3rem]' : 'w-[0.75rem] opacity-30'
            )}
          />
        ))}
      </div>
    </div>
  );
};

export default MarqueeCarousel;
